import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../ui/Icons';
import {
  type CollectionEnvelope,
  type DataEnvelope,
  type GuestActivation,
  type GuestOffer,
  GuestOffersApiError,
  type OfferContext,
  guestOffersFetch,
  offersEndpoint,
} from '../guestOffersApi';

type ActivationDetailScreenProps = {
  activationId: string;
  offerId: string | null;
  context: OfferContext;
  onBack: () => void;
  onRedeemed: () => void;
  onExpired: () => void;
};

function formatMoment(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function ActivationDetailScreen({
  activationId,
  offerId,
  context,
  onBack,
  onRedeemed,
  onExpired,
}: ActivationDetailScreenProps) {
  const [activation, setActivation] = useState<GuestActivation | null>(null);
  const [offerRules, setOfferRules] = useState<GuestOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [staffEntry, setStaffEntry] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const loadActivation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [mine, offers] = await Promise.all([
        guestOffersFetch<CollectionEnvelope<GuestActivation>>('/api/v1/me/offers'),
        guestOffersFetch<CollectionEnvelope<GuestOffer>>(offersEndpoint(context)).catch(() => ({ data: [] })),
      ]);
      const found = mine.data.find((item) => item.id === activationId) ?? null;

      if (!found) {
        setError('Не удалось найти эту активацию.');
        return;
      }

      if (found.status === 'redeemed') {
        onRedeemed();
        return;
      }

      if (found.status === 'expired' || found.status === 'revoked') {
        onExpired();
        return;
      }

      setActivation(found);
      setOfferRules(offers.data.find((item) => item.id === (offerId ?? found.offer.id)) ?? null);
    } catch (reason) {
      if (reason instanceof GuestOffersApiError && reason.status === 401) {
        setError('Сессия истекла. Войдите в Hopper снова.');
      } else {
        setError('Не удалось загрузить активированное предложение.');
      }
    } finally {
      setLoading(false);
    }
  }, [activationId, context.city, context.mode, context.venueId, offerId, onExpired, onRedeemed]);

  useEffect(() => {
    void loadActivation();
  }, [loadActivation]);

  const expiresLabel = useMemo(() => formatMoment(activation?.expires_at ?? null), [activation?.expires_at]);
  const startsLabel = useMemo(() => formatMoment(offerRules?.starts_at ?? null), [offerRules?.starts_at]);
  const endsLabel = useMemo(() => formatMoment(offerRules?.ends_at ?? null), [offerRules?.ends_at]);

  const redeem = useCallback(async () => {
    if (!/^\d{6}$/.test(code)) {
      setFlash('Введите шестизначный код от заведения');
      return;
    }

    setSubmitting(true);
    setFlash(null);

    try {
      const response = await guestOffersFetch<DataEnvelope<GuestActivation>>(
        `/api/v1/offer-activations/${encodeURIComponent(activationId)}/redeem`,
        {
          method: 'POST',
          body: JSON.stringify({
            code,
            entry_kind: staffEntry ? 'staff' : 'guest',
          }),
        },
      );

      setActivation(response.data);
      onRedeemed();
    } catch (reason) {
      if (reason instanceof GuestOffersApiError) {
        if (reason.status === 422 && reason.code === 'offer_code_invalid') {
          setFlash('Код не подошёл, попробуйте ещё раз');
          return;
        }

        if (reason.status === 409 && reason.code === 'offer_activation_expired') {
          onExpired();
          return;
        }

        if (reason.status === 409 && reason.code === 'offer_activation_redeemed') {
          onRedeemed();
          return;
        }

        if (reason.status === 429) {
          setFlash('Слишком много попыток, подождите минуту');
          return;
        }

        if (reason.status === 401) {
          setFlash('Сессия истекла. Войдите в Hopper снова.');
          return;
        }
      }

      setFlash('Не удалось подтвердить получение. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  }, [activationId, code, onExpired, onRedeemed, staffEntry]);

  if (loading) {
    return (
      <main className="activation-page">
        <div className="offers-state panel-card" role="status">
          <span className="map-state-card__pulse" />
          <strong>Открываем предложение</strong>
          <span>Проверяем вашу активацию.</span>
        </div>
      </main>
    );
  }

  if (error || !activation) {
    return (
      <main className="activation-page">
        <div className="offers-state panel-card">
          <strong>{error ?? 'Активация недоступна'}</strong>
          <button className="primary-button" type="button" onClick={() => void loadActivation()}>Попробовать снова</button>
          <button className="secondary-button" type="button" onClick={onBack}>В Предложения</button>
        </div>
      </main>
    );
  }

  return (
    <main className="activation-page" aria-label="Активированное предложение">
      <div className="activation-layout">
        <button className="offer-back-button" type="button" onClick={onBack}>← Предложения</button>

        <section className="activation-card panel-card">
          <div className="activation-heading">
            <span className="offers-eyebrow">Активировано</span>
            <h1>{activation.offer.title}</h1>
            <p><Icon name="location" /> {activation.venue.display_name ?? 'Заведение Hopper'}</p>
          </div>

          <div className="offer-live-panel">
            <span className="offer-live-label">Оффер активирован</span>
            <p className="offer-live-hint">
              Покажите этот экран сотруднику. После получения бонуса введите код заведения ниже.
            </p>
          </div>

          {expiresLabel && <div className="offer-expiry">Действует до: <strong>{expiresLabel}</strong></div>}

          {flash && <div className="offer-flash is-error" role="status">{flash}</div>}

          <div className="offer-redeem-form">
            <label htmlFor="offer-redemption-code">Код от заведения</label>
            <input
              id="offer-redemption-code"
              className="offer-code-input"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />

            <label className="offer-staff-toggle">
              <input
                type="checkbox"
                checked={staffEntry}
                onChange={(event) => setStaffEntry(event.target.checked)}
              />
              <span>Это сделал сотрудник на моём телефоне</span>
            </label>

            <button className="primary-button offer-redeem-submit" type="button" disabled={submitting} onClick={() => void redeem()}>
              {submitting ? 'Проверяем код…' : 'Подтвердить получение'}
            </button>
          </div>
        </section>

        <section className="activation-rules panel-card">
          <span className="offers-eyebrow">Условия</span>
          <h2>Что входит в предложение</h2>
          <p>{activation.offer.description}</p>

          {(startsLabel || endsLabel) && (
            <dl>
              {startsLabel && <div><dt>Начало</dt><dd>{startsLabel}</dd></div>}
              {endsLabel && <div><dt>До</dt><dd>{endsLabel}</dd></div>}
            </dl>
          )}

          <div className="activation-rules__venue">
            <Icon name="location" />
            <div>
              <strong>{activation.venue.display_name ?? 'Заведение Hopper'}</strong>
              <span>{activation.venue.address ?? 'Адрес уточняется'}</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
